import React from 'react';
import {
    AutocompleteArrayInput,
    Create,
    Edit,
    FormDataConsumer,
    SimpleForm,
    useRecordContext,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {Box, Typography} from '@mui/material';
import {DeleteOutline} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useFetchUsers} from '../lib/customHooks';

const ACTION_TYPE_CHOICES = [
    {id: 'NATIVE', name: 'Native'},
    {id: 'WEBVIEW', name: 'WebView'},
];

const DEVICE_TYPE_CHOICES = [
    {id: 'ALL', name: 'All'},
    {id: 'ANDROID', name: 'Android'},
    {id: 'IOS', name: 'iOS'},
];

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const HeaderBar = ({mode, record, translate, onDelete}) => {
    const identifier = record?.action_identifier || 'RN action';

    return (
        <PageHeader
            breadcrumb={{label: 'RN Actions', to: '/rnActions'}}
            title={
                mode === 'create' ? (
                    <Box component="span">Create RN action</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record?.active ? 'success' : 'neutral'}/>
                        <Typography component="span" sx={{fontFamily: t.font.mono}}>
                            {identifier}
                        </Typography>
                    </Box>
                )
            }
            actions={
                <>
                    <SaveAction label={mode === 'create' ? 'Create RN action' : 'Save changes'}/>
                    {mode === 'edit' && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const deleteConfirm = useDeleteConfirm();
    const userChoices = useFetchUsers();

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                onDelete={deleteConfirm.openDialog}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="rnActions"
                    record={record}
                    recordLabel={record.action_identifier}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title="Action">
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr', gap: 2}}>
                            <StatamicTextInput
                                source="action_identifier"
                                label="Action identifier"
                                required
                            />
                        </Box>
                        <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicSelectInput
                                source="action_type"
                                label="Action type"
                                required
                                choices={ACTION_TYPE_CHOICES}
                            />
                            <StatamicSelectInput
                                source="device_type"
                                label="Device type"
                                required
                                choices={DEVICE_TYPE_CHOICES}
                            />
                        </Box>
                        <Box sx={{mt: 2}}>
                            <StatamicTextInput source="url" label="URL" type="url"/>
                        </Box>
                    </SectionCard>

                    <SectionCard title="Audience">
                        <ToggleRow
                            source="limit_to_users"
                            label="Limit to specific users"
                            subtitle="When enabled, only the selected users receive this action"
                            defaultValue={false}
                        />
                        <FormDataConsumer>
                            {({formData}) => formData.limit_to_users && (
                                <Box sx={{mt: 2}}>
                                    <AutocompleteArrayInput
                                        source="user_ids"
                                        label={translate('Users')}
                                        choices={userChoices || []}
                                        placeholder={translate('Search and select users')}
                                        fullWidth
                                    />
                                </Box>
                            )}
                        </FormDataConsumer>
                    </SectionCard>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title={translate('Status')}>
                        <ToggleRow
                            source="active"
                            label={translate('Is active')}
                            subtitle="Disable to hide this action from the mobile app"
                            defaultValue={true}
                        />
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

export const RNActionEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="edit">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const RNActionCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="edit">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default RNActionEditStatamic;
